<template lang="pug">
.organiser-wizard
  header.wizard-topbar
    a.wizard-brand(href="/") REGPLACE
    span.wizard-save-status(v-if="authenticated" role="status" aria-live="polite") {{ saveStatus }}
    a.wizard-settings-link(v-if="authenticated && eventId && currentStep !== 'success' && summary.admin_url" :href="summary.admin_url" :aria-disabled="loading ? 'true' : null" @click.prevent="goToAdvanced(summary.admin_url)") Все настройки
    a.wizard-help(href="/mail" target="_blank" rel="noopener" @click="trackAction('help')") Помощь

  nav.wizard-progress(v-if="authenticated && currentStep !== 'success'" aria-label="Шаги создания мероприятия")
    button.wizard-progress__step(
      v-for="(step, index) in steps"
      :key="step.key"
      type="button"
      :class="{ 'is-current': currentStep === step.key, 'is-done': stepIndex > index }"
      :aria-current="currentStep === step.key ? 'step' : null"
      :aria-label="step.title"
      :disabled="loading || index >= stepIndex"
      @click="goToStep(step.key)"
    )
      span.wizard-progress__number {{ index + 1 }}
      span.wizard-progress__label {{ step.title }}
    p.wizard-progress__mobile Шаг {{ stepIndex + 1 }} из 3 · {{ stepTitle }}

  main.wizard-main
    header.wizard-heading
      p.wizard-eyebrow {{ currentStep === 'success' ? 'Кабинет организатора' : 'Ваше первое мероприятие на REGPLACE' }}
      h1(ref="stepHeading" tabindex="-1") {{ heading }}
      p.wizard-intro {{ intro }}

    .wizard-error(v-if="errorMessages.length" ref="errorSummary" role="alert" tabindex="-1")
      strong Проверьте данные
      ul
        li(v-for="(message, index) in errorMessages" :key="index") {{ message }}

    form(v-if="currentStep === 'account'" @submit.prevent="submitStep")
      fieldset(:disabled="loading")
        .wizard-account-modes(aria-label="Вход или создание аккаунта")
          button(type="button" :aria-pressed="form.account.mode === 'register'" @click="setAccountMode('register')") Создать аккаунт
          button(type="button" :aria-pressed="form.account.mode === 'login'" @click="setAccountMode('login')") Уже есть аккаунт
        .wizard-field
          label(for="organiser-email") Email
          input.ym-disable-keys#organiser-email(v-model.trim="form.account.email" type="email" autocomplete="email" required autofocus)
          field-errors(:messages="fieldErrors('email')")
        .wizard-field
          label(for="organiser-password") Пароль
          .wizard-password
            input.ym-disable-keys#organiser-password(v-model="form.account.password" :type="showPassword ? 'text' : 'password'" :autocomplete="form.account.mode === 'login' ? 'current-password' : 'new-password'" required)
            button(type="button" :aria-pressed="showPassword" @click="showPassword = !showPassword") {{ showPassword ? 'Скрыть' : 'Показать' }}
          field-errors(:messages="fieldErrors('password')")
          a.wizard-text-link(v-if="form.account.mode === 'login'" href="/reset_password") Восстановить пароль
        p.wizard-note После входа черновик сохранится в аккаунте. Вы сможете продолжить с телефона или компьютера.
      footer.wizard-actions
        span
        button.wizard-button.wizard-button--primary(type="submit" :disabled="loading") {{ loading ? 'Сохраняем…' : (form.account.mode === 'login' ? 'Войти и продолжить' : 'Создать аккаунт и продолжить') }}

    form(v-else-if="currentStep === 'event'" @submit.prevent="submitStep")
      fieldset(:disabled="loading")
        .wizard-field
          label(for="event-name") Название мероприятия
          input.ym-disable-keys#event-name(v-model="form.event.name" type="text" placeholder="Например, Осенний забег в парке" required maxlength="250")
          field-errors(:messages="fieldErrors('name')")
        .wizard-field
          label(for="event-lead") Короткое описание
          textarea.ym-disable-keys#event-lead(v-model="form.event.lead" rows="3" placeholder="Что будет происходить и для кого это мероприятие" required)
          p.wizard-hint Достаточно двух-трёх предложений. Подробности можно добавить позже.
          field-errors(:messages="fieldErrors('lead')")
        .wizard-grid
          .wizard-field
            label(for="event-start") Дата начала
            input.ym-disable-keys#event-start(v-model="form.event.start_date" type="date" required)
            field-errors(:messages="fieldErrors('start_date')")
          .wizard-field
            label(for="event-end") Дата окончания <span>если несколько дней</span>
            input.ym-disable-keys#event-end(v-model="form.event.end_date" type="date" :min="form.event.start_date || null")
            field-errors(:messages="fieldErrors('end_date')")
        .wizard-grid
          .wizard-field
            label(for="event-city") Город
            input.ym-disable-keys#event-city(v-model="form.event.city" type="text" autocomplete="address-level2" required)
            field-errors(:messages="fieldErrors('city')")
          .wizard-field
            label(for="event-address") Место проведения
            input.ym-disable-keys#event-address(v-model="form.event.address" type="text" placeholder="Название площадки или адрес" required)
            field-errors(:messages="fieldErrors('address')")
        .wizard-field
          label(for="event-cover") Обложка <span>можно добавить позже</span>
          .wizard-cover
            img(v-if="coverPreview" :src="coverPreview" alt="Обложка мероприятия")
            .wizard-cover__controls
              input.ym-disable-keys#event-cover(ref="coverInput" type="file" accept="image/jpeg,image/png" @change="selectCover")
              p.wizard-hint JPG или PNG, до 5 МБ. Рекомендуем горизонтальное изображение от 1600 × 960 пикселей.
              p.wizard-hint(v-if="coverFile") Фото загрузится при переходе к следующему шагу.
          field-errors(:messages="fieldErrors('cover')")
        section.wizard-section
          h2 Контакты для участников
          p.wizard-hint Email и телефон появятся на странице мероприятия.
          p.wizard-hint(v-if="summary.company_name") Организатор на странице: {{ summary.company_name }}.
          .wizard-field
            label(for="event-contact") Контактное лицо
            input.ym-disable-keys#event-contact(v-model="form.event.contact_name" type="text" autocomplete="name" required)
            p.wizard-hint Имя человека, с которым можно обсудить мероприятие.
            field-errors(:messages="fieldErrors('contact_name')")
          .wizard-grid
            .wizard-field
              label(for="event-contact-email") Email для вопросов
              input.ym-disable-keys#event-contact-email(v-model.trim="form.event.email" type="email" autocomplete="email" required)
              field-errors(:messages="fieldErrors('email')")
            .wizard-field
              label(for="event-phone") Телефон для вопросов
              input.ym-disable-keys#event-phone(v-model="form.event.phone" type="tel" autocomplete="tel" placeholder="+7 900 000-00-00" required)
              field-errors(:messages="fieldErrors('phone')")
      footer.wizard-actions
        a.wizard-text-link(v-if="summary.admin_url" :href="summary.admin_url" @click.prevent="goToAdvanced(summary.admin_url)") Все настройки
        span(v-else)
        button.wizard-button.wizard-button--primary(type="submit" :disabled="loading") {{ loading ? 'Сохраняем…' : 'Далее: участие и регистрация' }}

    form(v-else-if="currentStep === 'registration'" @submit.prevent="submitStep")
      fieldset(:disabled="loading")
        .wizard-field
          label(for="registration-sport") Вид спорта или формат
          select#registration-sport.ym-disable-keys(v-model="form.registration.sport_id" :disabled="form.registration.advanced_sports" required)
            option(disabled value="") Выберите из списка
            option(v-for="sport in sports" :key="sport.id" :value="sport.id") {{ sport.name }}
          template(v-if="form.registration.advanced_sports")
            p.wizard-hint У вариантов участия разные виды спорта. Измените их во всех настройках.
            a.wizard-text-link(:href="summary.admin_url" @click.prevent="goToAdvanced(summary.admin_url)") Все настройки мероприятия
          p.wizard-hint(v-else) Для неспортивного мероприятия выберите «Другой вид».
          field-errors(:messages="fieldErrors('sport_id')")
        .wizard-field
          span.wizard-label Участие
          .wizard-choices
            label.wizard-choice(:class="{ 'is-selected': form.registration.free_participation }")
              input.ym-disable-keys(v-model="form.registration.free_participation" type="radio" :value="true" :disabled="hasAdvancedPrices")
              span Бесплатное
            label.wizard-choice(:class="{ 'is-selected': !form.registration.free_participation }")
              input.ym-disable-keys(v-model="form.registration.free_participation" type="radio" :value="false" :disabled="hasAdvancedPrices")
              span Платное
          p.wizard-hint(v-if="hasAdvancedPrices") У вариантов участия есть особые условия оплаты. Изменить их можно во всех настройках.
        .wizard-notice(v-if="!form.registration.free_participation")
          strong Онлайн-оплата
          p {{ summary.company_verified ? 'Оплата подключена. Участники смогут оплатить заявку после открытия регистрации.' : 'Страницу можно опубликовать сразу. Для открытия платной регистрации потребуется подключить и проверить реквизиты организатора.' }}
          a.wizard-text-link(v-if="!summary.company_verified && summary.company_url" :href="summary.company_url" target="_blank" rel="noopener" @click="trackAction('payment')") Подключить оплату
        section.wizard-section
          h2 Варианты участия
          p.wizard-hint Например, «5 км», «10 км» или «Участие в мастер-классе». Для каждого участника создаётся отдельная заявка.
          .wizard-race(v-for="(race, index) in form.registration.races" :key="race.id || race.client_key")
            .wizard-race__heading
              h3 Вариант {{ index + 1 }}
              button.wizard-text-button(v-if="!race.id && form.registration.races.length > 1" type="button" @click="removeRace(index)") Удалить
            .wizard-field
              label(:for="'race-name-' + index") Название
              input.ym-disable-keys(:id="'race-name-' + index" v-model="race.name" type="text" placeholder="Например, 5 км" required)
              field-errors(:messages="raceErrors(index, 'name')")
            .wizard-grid
              .wizard-field(v-if="!form.registration.free_participation")
                label(:for="'race-price-' + index") Стоимость, ₽
                template(v-if="race.advanced_price")
                  p.wizard-readonly {{ advancedText(race.advanced_price, 'Особые условия оплаты') }}
                  a.wizard-text-link(:href="race.advanced_url || summary.admin_url" @click.prevent="goToAdvanced(race.advanced_url || summary.admin_url)") Изменить стоимость
                template(v-else)
                  input.ym-disable-keys(:id="'race-price-' + index" v-model="race.price_rub" type="number" inputmode="decimal" min="0.01" step="0.01" required)
                  field-errors(:messages="raceErrors(index, 'price_rub')")
              .wizard-field
                label(:for="'race-capacity-' + index") Количество мест <span>необязательно</span>
                template(v-if="race.advanced_limits")
                  p.wizard-readonly {{ advancedText(race.advanced_limits, 'Особые ограничения') }}
                  a.wizard-text-link(:href="race.advanced_url || summary.admin_url" @click.prevent="goToAdvanced(race.advanced_url || summary.admin_url)") Изменить ограничения
                template(v-else)
                  input.ym-disable-keys(:id="'race-capacity-' + index" v-model="race.capacity" type="number" inputmode="numeric" min="1" step="1" placeholder="Без ограничения")
                  field-errors(:messages="raceErrors(index, 'capacity')")
          button.wizard-button.wizard-button--secondary(type="button" @click="addRace") + Добавить вариант участия
          field-errors(:messages="fieldErrors('races')")
        section.wizard-section
          h2 Когда принимать заявки
          .wizard-choices
            label.wizard-choice(:class="{ 'is-selected': form.registration.opening_mode === 'now' }")
              input.ym-disable-keys(v-model="form.registration.opening_mode" type="radio" value="now")
              span После публикации
            label.wizard-choice(:class="{ 'is-selected': form.registration.opening_mode === 'scheduled' }")
              input.ym-disable-keys(v-model="form.registration.opening_mode" type="radio" value="scheduled")
              span С выбранной даты
          .wizard-grid.wizard-dates
            .wizard-field(v-if="form.registration.opening_mode === 'scheduled'")
              label(for="registration-start") Открыть регистрацию
              input.ym-disable-keys#registration-start(v-model="form.registration.registration_start" type="datetime-local" required)
              field-errors(:messages="fieldErrors('registration_start')")
            .wizard-field
              label(for="registration-end") Закрыть регистрацию
              input.ym-disable-keys#registration-end(v-model="form.registration.registration_end" type="datetime-local" required)
              field-errors(:messages="fieldErrors('registration_end')")
          p.wizard-hint Время указано по Москве. Перед запуском вы сможете проверить страницу.
        details.wizard-form-preview
          summary Что заполняет участник
          p.wizard-hint Базовая анкета уже подготовлена. Эти шесть полей обязательны.
          ul
            li(v-for="field in participantFields" :key="field") {{ field }}
          a.wizard-text-link(v-if="summary.form_preview_url && hasSavedRaces" :href="summary.form_preview_url" target="_blank" rel="noopener") Посмотреть форму участника ↗
          p.wizard-hint(v-else) Предпросмотр появится после сохранения вариантов участия.
          p.wizard-hint Дополнительные вопросы, команды и особые условия доступны во всех настройках.
      footer.wizard-actions
        button.wizard-text-button(type="button" :disabled="loading" @click="goToStep('event')") Назад
        button.wizard-button.wizard-button--primary(type="submit" :disabled="loading") {{ loading ? 'Сохраняем…' : 'Далее: проверить и опубликовать' }}

    form(v-else-if="currentStep === 'confirm'" @submit.prevent="submitStep")
      .wizard-review
        img.wizard-review__cover(v-if="coverPreview" :src="coverPreview" alt="Обложка мероприятия")
        .wizard-review__content
          h2 {{ form.event.name }}
          p {{ form.event.lead }}
          dl.wizard-facts
            div
              dt Когда
              dd {{ eventDateLabel }}
            div
              dt Где
              dd {{ [form.event.city, form.event.address].filter(Boolean).join(', ') }}
            div
              dt Участие
              dd {{ form.registration.free_participation ? 'Бесплатное' : 'Платное' }} · {{ form.registration.races.length }} {{ variantsLabel }}
          button.wizard-text-button(type="button" :disabled="loading" @click="goToStep('event')") Изменить информацию
          ul.wizard-review-races
            li(v-for="race in form.registration.races" :key="race.id || race.client_key")
              strong {{ race.name }}
              span {{ racePriceLabel(race) }}
              small(v-if="race.advanced_limits") {{ advancedText(race.advanced_limits, 'Особые ограничения') }}
              small(v-else-if="race.capacity") Мест: {{ race.capacity }}
          button.wizard-text-button(type="button" :disabled="loading" @click="goToStep('registration')") Изменить условия участия
      .wizard-preview-links
        a.wizard-button.wizard-button--secondary(v-if="summary.preview_url" :href="summary.preview_url" target="_blank" rel="noopener") Посмотреть страницу ↗
        a.wizard-text-link(v-if="summary.form_preview_url && hasSavedRaces" :href="summary.form_preview_url" target="_blank" rel="noopener") Посмотреть форму участника ↗
      .wizard-notice
        strong {{ launchNoticeTitle }}
        p {{ launchNotice }}
        a.wizard-text-link(v-if="paid && !summary.payment_ready && summary.company_url" :href="summary.company_url" target="_blank" rel="noopener" @click="trackAction('payment')") Подключить оплату
      .wizard-error(v-if="launchBlockers.length" role="status")
        strong До запуска осталось
        ul
          li(v-for="(message, index) in launchBlockers" :key="index") {{ message }}
      button.wizard-text-button(v-if="paid && !summary.payment_ready" type="button" :disabled="loading" @click="refreshState") Проверить статус оплаты
      footer.wizard-actions
        button.wizard-text-button(type="button" :disabled="loading" @click="goToStep('registration')") Назад
        button.wizard-button.wizard-button--primary(type="submit" :disabled="loading || !canSubmitLaunch") {{ loading ? 'Сохраняем…' : launchLabel }}

    section.wizard-cabinet(v-else-if="currentStep === 'success'")
      .wizard-notice(:class="{ 'wizard-notice--success': summary.published }")
        strong {{ cabinetStatusTitle }}
        p {{ cabinetStatusDescription }}
      .wizard-share(v-if="summary.published && summary.event_url")
        label(for="event-share-link") Ссылка для участников
        input.ym-disable-keys#event-share-link(ref="shareLink" :value="publicEventUrl" readonly @focus="$event.target.select()")
        .wizard-inline-actions
          button.wizard-button.wizard-button--primary(type="button" @click="shareEvent") {{ copySuccess ? 'Ссылка скопирована' : 'Поделиться ссылкой' }}
          a.wizard-text-link(:href="summary.event_url" target="_blank" rel="noopener") Открыть страницу ↗
      .wizard-stats
        a.wizard-stat(:href="summary.participants_url || summary.admin_url")
          strong {{ summary.registrations_count || 0 }}
          span Заявки участников
        .wizard-stat
          strong {{ summary.interests_count || 0 }}
          span Ждут открытия регистрации
      .wizard-notice(v-if="paid && !summary.payment_ready")
        strong Подключите оплату, чтобы открыть регистрацию
        p Страница уже доступна по ссылке. После проверки реквизитов вернитесь сюда и откройте регистрацию.
        .wizard-inline-actions
          a.wizard-button.wizard-button--primary(v-if="summary.company_url" :href="summary.company_url" target="_blank" rel="noopener" @click="trackAction('payment')") Подключить оплату
          button.wizard-text-button(type="button" :disabled="loading" @click="refreshState") {{ loading ? 'Проверяем…' : 'Проверить статус' }}
      .wizard-notice(v-else-if="summary.published && summary.launch_requested && form.registration.opening_mode === 'scheduled' && summary.stage === 'planned'")
        strong Открытие запланировано
        p Регистрация откроется {{ formatDateTime(form.registration.registration_start) }} по Москве.
        button.wizard-text-button(type="button" :disabled="loading" @click="goToStep('registration')") Изменить дату открытия
      .wizard-notice(v-else-if="summary.published && summary.stage !== 'open' && summary.stage !== 'finished'")
        strong {{ form.registration.opening_mode === 'scheduled' ? 'Открытие регистрации' : 'Можно принимать заявки' }}
        p {{ form.registration.opening_mode === 'scheduled' ? 'Проверьте дату открытия перед запуском.' : 'Откройте регистрацию, когда будете готовы принимать участников.' }}
        ul(v-if="launchBlockers.length")
          li(v-for="(message, index) in launchBlockers" :key="index") {{ message }}
        button.wizard-button.wizard-button--primary(type="button" :disabled="loading || !canSubmitLaunch" @click="submitStep") {{ loading ? 'Сохраняем…' : launchLabel }}
      nav.wizard-cabinet-actions(aria-label="Управление мероприятием")
        button.wizard-button.wizard-button--secondary(type="button" :disabled="loading" @click="goToStep('event')") Изменить страницу
        button.wizard-button.wizard-button--secondary(type="button" :disabled="loading" @click="goToStep('registration')") Участие и регистрация
        a.wizard-button.wizard-button--secondary(v-if="summary.participants_url" :href="summary.participants_url") Список участников
        a.wizard-text-link(v-if="summary.admin_url" :href="summary.admin_url" @click.prevent="goToAdvanced(summary.admin_url)") Все настройки мероприятия →
      p.wizard-hint(v-if="!summary.registrations_count") Поделитесь ссылкой с будущими участниками. Здесь появятся первые заявки.
</template>

<script>
import { createOnboardingAnalytics } from '../../lib/organiser-onboarding-analytics';

const FieldErrors = {
  props: { messages: { type: Array, default: () => [] } },
  render(h) {
    return this.messages.length ? h('p', { class: 'wizard-field-error' }, this.messages.join(', ')) : null;
  }
};

const eventDefaults = () => ({ name: '', lead: '', start_date: '', end_date: '', city: '', address: '', contact_name: '', email: '', phone: '', cover_url: '' });
const newRace = () => ({ client_key: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`, name: '', price_rub: '', capacity: '' });
const registrationDefaults = () => ({ sport_id: '', free_participation: true, opening_mode: 'now', registration_start: '', registration_end: '', races: [newRace()] });
const clone = value => JSON.parse(JSON.stringify(value));
const fieldLabels = { email: 'Email', password: 'Пароль', name: 'Название', lead: 'Описание', start_date: 'Дата начала', end_date: 'Дата окончания', city: 'Город', address: 'Место проведения', contact_name: 'Контакт организатора', phone: 'Телефон', sport_id: 'Вид спорта', registration_start: 'Открытие регистрации', registration_end: 'Закрытие регистрации', price_rub: 'Стоимость', capacity: 'Количество мест', cover: 'Обложка', races: 'Варианты участия' };

export default {
  name: 'OrganiserWizard',
  components: { FieldErrors },
  props: {
    state: { type: Object, default: () => ({}) },
    sports: { type: Array, default: () => [] },
    existingUser: { type: Boolean, default: false }
  },
  data() {
    const authenticated = Boolean(this.state.authenticated || this.existingUser);
    const registration = Object.assign(registrationDefaults(), this.state.registration || {});
    registration.races = (registration.races.length ? registration.races : [newRace()]).map(race => Object.assign(newRace(), race));
    return {
      authenticated,
      currentStep: authenticated ? (this.state.current_step || 'event') : 'account',
      eventId: this.state.event_id || null,
      form: {
        account: { email: (this.state.account || {}).email || '', password: '', mode: 'register' },
        event: Object.assign(eventDefaults(), this.state.event || {}),
        registration
      },
      summary: this.state.summary || {},
      savedDraftSections: this.state.pending_sections || [],
      loading: false,
      errors: {},
      generalError: '',
      showPassword: false,
      hydrating: false,
      saveStatus: 'Черновик сохранён в аккаунте',
      dirty: false,
      coverFile: null,
      coverObjectUrl: '',
      copySuccess: false,
      participantFields: ['Фамилия', 'Имя', 'Пол', 'Дата рождения', 'Email', 'Телефон'],
      steps: [{ key: 'event', title: 'Мероприятие' }, { key: 'registration', title: 'Участие' }, { key: 'confirm', title: 'Публикация' }]
    };
  },
  computed: {
    stepIndex() { return this.steps.findIndex(step => step.key === this.currentStep); },
    stepTitle() { return (this.steps[this.stepIndex] || {}).title || ''; },
    heading() {
      return { account: 'Начнём с вашего аккаунта', event: 'Расскажите о мероприятии', registration: 'Как участвовать?', confirm: 'Всё готово к публикации?', success: this.summary.event_name || this.form.event.name || 'Ваше мероприятие' }[this.currentStep];
    },
    intro() {
      return { account: 'Создайте страницу и настройте регистрацию за три простых шага.', event: 'Добавьте главное, что нужно знать будущим участникам.', registration: 'Укажите варианты участия, стоимость и даты приёма заявок.', confirm: 'Посмотрите на страницу глазами участника и проверьте условия запуска.', success: 'Страница, участники и следующий шаг — всё под рукой.' }[this.currentStep];
    },
    errorMessages() {
      const messages = this.generalError ? [this.generalError] : [];
      Object.entries(this.errors).forEach(([key, values]) => {
        [].concat(values || []).forEach(value => messages.push((fieldLabels[key] ? `${fieldLabels[key]}: ` : '') + value));
      });
      return messages;
    },
    coverPreview() { return this.coverObjectUrl || this.form.event.cover_url; },
    publicEventUrl() { return this.summary.event_url ? new URL(this.summary.event_url, window.location.origin).href : ''; },
    paid() { return !this.form.registration.free_participation; },
    hasAdvancedPrices() { return this.form.registration.races.some(race => race.advanced_price); },
    hasSavedRaces() { return this.form.registration.races.some(race => Boolean(race.id)); },
    launchIntent() {
      if (['publish', 'open', 'schedule'].includes(this.summary.next_launch_intent)) return this.summary.next_launch_intent;
      if (this.paid && !this.summary.payment_ready) return 'publish';
      return this.form.registration.opening_mode === 'scheduled' ? 'schedule' : 'open';
    },
    launchLabel() {
      if (this.launchIntent === 'publish') return this.summary.published ? 'Сохранить изменения' : 'Опубликовать страницу';
      if (this.summary.published && this.summary.stage === 'open') return 'Сохранить изменения';
      if (this.launchIntent === 'schedule') return this.summary.published ? 'Запланировать открытие' : 'Опубликовать и запланировать открытие';
      return this.summary.published ? 'Открыть регистрацию' : 'Опубликовать и открыть регистрацию';
    },
    launchNoticeTitle() {
      if (this.launchIntent === 'publish') return this.summary.published ? 'Страница доступна участникам' : 'Сейчас опубликуем страницу';
      return this.launchIntent === 'schedule' ? 'Регистрация откроется в выбранное время' : 'Участники смогут сразу подать заявку';
    },
    launchNotice() {
      if (this.launchIntent === 'publish') return 'Участники увидят описание и смогут ждать открытия. Платная регистрация откроется после проверки реквизитов и вашего подтверждения.';
      if (this.launchIntent === 'schedule') return `Страница станет доступна сразу. Приём заявок начнётся ${this.formatDateTime(this.form.registration.registration_start)} и закончится ${this.formatDateTime(this.form.registration.registration_end)}.`;
      return `Страница станет доступна по ссылке, а регистрация откроется сразу. Приём заявок закончится ${this.formatDateTime(this.form.registration.registration_end)}.`;
    },
    launchBlockers() {
      const errors = [].concat(this.summary.publication_errors || []);
      if (this.launchIntent !== 'publish') errors.push(...[].concat(this.summary.registration_errors || []));
      return Array.from(new Set(errors.filter(Boolean)));
    },
    canSubmitLaunch() {
      if (this.launchBlockers.length) return false;
      return this.launchIntent === 'publish' || this.summary.can_launch === true;
    },
    eventDateLabel() {
      const start = this.formatDate(this.form.event.start_date);
      return this.form.event.end_date && this.form.event.end_date !== this.form.event.start_date ? `${start} — ${this.formatDate(this.form.event.end_date)}` : start;
    },
    variantsLabel() {
      const count = this.form.registration.races.length;
      return count % 10 === 1 && count % 100 !== 11 ? 'вариант' : (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14) ? 'варианта' : 'вариантов');
    },
    cabinetStatusTitle() {
      if (!this.summary.published) return 'Черновик сохранён';
      if (this.summary.stage === 'open') return 'Страница опубликована, регистрация открыта';
      if (this.summary.stage === 'closed') return 'Регистрация закрыта';
      if (this.summary.stage === 'finished') return 'Мероприятие завершено';
      return 'Страница опубликована';
    },
    cabinetStatusDescription() {
      if (!this.summary.published) return 'Продолжите настройку, когда будет удобно.';
      if (this.summary.stage === 'open') return 'Поделитесь ссылкой. Участники уже могут подавать заявки.';
      if (this.summary.stage === 'closed' || this.summary.stage === 'finished') return 'Заявки и настройки мероприятия доступны ниже.';
      if (this.paid && !this.summary.payment_ready) return 'Можно делиться страницей и собирать интерес. Для приёма платных заявок осталось подключить оплату.';
      return this.form.registration.opening_mode === 'scheduled' && this.summary.launch_requested ? `Регистрация откроется ${this.formatDateTime(this.form.registration.registration_start)}.` : 'Регистрацию можно открыть, когда будете готовы.';
    }
  },
  watch: {
    'form.event': { deep: true, handler() { this.scheduleAutosave('event'); } },
    'form.registration': { deep: true, handler() { this.scheduleAutosave('registration'); } },
    currentStep(newStep) {
      this.analytics.stepView(newStep);
      this.focusHeading();
    }
  },
  created() {
    this.analytics = createOnboardingAnalytics((goal, params, callback) => {
      if (typeof window.trackMetrikaGoal !== 'function') return false;
      window.trackMetrikaGoal(goal, params, callback);
      return true;
    });
    this.requestQueue = Promise.resolve();
    this.draftFlush = null;
    this.pendingSections = new Set();
    this.autosaveTimer = null;
  },
  mounted() {
    this.analytics.pageView(this.currentStep);
    this.analytics.stepView(this.currentStep);
    window.addEventListener('beforeunload', this.warnUnsaved);
  },
  beforeDestroy() {
    window.clearTimeout(this.autosaveTimer);
    window.clearTimeout(this.copyTimer);
    window.removeEventListener('beforeunload', this.warnUnsaved);
    if (this.coverObjectUrl) URL.revokeObjectURL(this.coverObjectUrl);
  },
  methods: {
    fieldErrors(field) { return [].concat(this.errors[field] || []); },
    raceErrors(index, field) { return this.fieldErrors(`races.${index}.${field}`).concat(this.fieldErrors(`races[${index}].${field}`)); },
    advancedText(value, fallback) { return typeof value === 'string' ? value : (value && value.summary) || fallback; },
    racePriceLabel(race) {
      if (this.form.registration.free_participation) return 'Бесплатно';
      if (race.advanced_price) return this.advancedText(race.advanced_price, 'По условиям оплаты');
      const participantPrice = race.participant_price_rub;
      const amount = participantPrice !== undefined && participantPrice !== null ? participantPrice : race.price_rub;
      const formatted = Number(amount).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
      return `${participantPrice !== undefined && participantPrice !== null ? 'Участник заплатит' : 'Базовая стоимость'}: ${formatted} ₽`;
    },
    setAccountMode(mode) { this.form.account.mode = mode; this.clearErrors(); },
    clearErrors() { this.errors = {}; this.generalError = ''; },
    endpoint(path) {
      return this.eventId && path !== '/register-org/steps/account' ? `${path}?event_id=${encodeURIComponent(this.eventId)}` : path;
    },
    request(method, path, data, options = {}) {
      const task = this.requestQueue.catch(() => {}).then(() => this.axios(Object.assign({}, options, { method, url: this.endpoint(path), data }))).then(response => {
        this.updateCsrf(response.data.csrf_token);
        if (response.data.state && Array.isArray(response.data.state.pending_sections)) this.savedDraftSections = response.data.state.pending_sections;
        if (response.data.success === false) throw { response };
        this.analytics.response(response.data);
        return response.data;
      });
      this.requestQueue = task;
      return task;
    },
    updateCsrf(token) {
      if (!token) return;
      this.axios.defaults.headers.common['X-CSRF-Token'] = token;
      const tag = document.querySelector('meta[name="csrf-token"]');
      if (tag) tag.setAttribute('content', token);
    },
    hydrateState(state, preserveStep = false) {
      if (!state) return;
      this.hydrating = true;
      if (state.authenticated !== undefined) this.authenticated = state.authenticated;
      if (state.event_id) this.eventId = state.event_id;
      if (state.account) this.form.account.email = state.account.email || this.form.account.email;
      if (state.event) this.form.event = Object.assign(eventDefaults(), this.form.event, state.event);
      if (state.registration) {
        const registration = Object.assign(registrationDefaults(), this.form.registration, state.registration);
        registration.races = (registration.races.length ? registration.races : [newRace()]).map(race => Object.assign(newRace(), race));
        this.form.registration = registration;
      }
      if (state.summary) this.summary = state.summary;
      if (Array.isArray(state.pending_sections)) this.savedDraftSections = state.pending_sections;
      if (!preserveStep && state.current_step) this.currentStep = state.current_step;
      this.$nextTick(() => { this.hydrating = false; });
    },
    sectionPayload(section) {
      if (section === 'event') {
        const payload = clone(this.form.event);
        delete payload.cover_url;
        return payload;
      }
      const payload = clone(this.form.registration);
      delete payload.advanced_sports;
      payload.races = payload.races.map(race => {
        const result = { id: race.id, client_key: race.client_key, name: race.name };
        if (!race.advanced_price) result.price_rub = race.price_rub;
        if (!race.advanced_limits) result.capacity = race.capacity;
        return result;
      });
      return payload;
    },
    scheduleAutosave(section) {
      if (!this.authenticated || this.hydrating || this.loading) return;
      this.pendingSections.add(section);
      this.dirty = true;
      this.saveStatus = 'Сохраняем изменения…';
      window.clearTimeout(this.autosaveTimer);
      this.autosaveTimer = window.setTimeout(() => this.flushDraft().catch(() => {}), 800);
    },
    async flushDraft() {
      window.clearTimeout(this.autosaveTimer);
      while (this.draftFlush) await this.draftFlush;
      if (!this.pendingSections.size) return;

      const flush = this.savePendingDraft();
      this.draftFlush = flush;
      try { await flush; }
      finally { this.draftFlush = null; }
    },
    async savePendingDraft() {
      const sections = Array.from(this.pendingSections);
      this.pendingSections.clear();
      for (const [index, section] of sections.entries()) {
        const data = this.sectionPayload(section);
        try {
          const response = await this.request('patch', '/register-org/draft', { section, data });
          if (response.state && response.state.event_id) this.eventId = response.state.event_id;
          if (response.state && response.state.summary) this.summary = response.state.summary;
        } catch (error) {
          sections.slice(index).forEach(pending => this.pendingSections.add(pending));
          this.dirty = true;
          this.saveStatus = 'Не удалось сохранить. Нажмите «Далее», чтобы повторить.';
          throw error;
        }
      }
      if (!this.pendingSections.size) {
        this.dirty = false;
        this.saveStatus = 'Черновик сохранён в аккаунте';
      }
    },
    async goToStep(step) {
      if (this.loading) return;
      this.clearErrors();
      try {
        await this.flushDraft();
        this.currentStep = step;
      } catch (error) { this.showError(error); }
    },
    async saveSection(section) {
      const response = await this.request('post', `/register-org/steps/${section}`, { [section]: this.sectionPayload(section) });
      this.hydrateState(response.state, true);
      if (section === 'event') await this.uploadCover();
      return response;
    },
    async goToAdvanced(url) {
      if (this.loading || !url) return;
      const sourceStep = this.currentStep;
      this.loading = true;
      this.clearErrors();
      try {
        await this.flushDraft();
        const sections = ['event', 'registration'].filter(section => this.savedDraftSections.includes(section) || (section === 'event' && this.coverFile));
        for (const section of sections) {
          this.currentStep = section;
          await this.saveSection(section);
        }
        await this.analytics.beforeNavigate('expert', { step: sourceStep });
        window.location.assign(url);
      } catch (error) { this.showError(error); }
      finally { this.loading = false; }
    },
    addRace() { this.form.registration.races.push(newRace()); },
    removeRace(index) { this.form.registration.races.splice(index, 1); },
    selectCover(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.$delete(this.errors, 'cover');
      if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > 5 * 1024 * 1024) {
        this.coverFile = null;
        if (this.coverObjectUrl) URL.revokeObjectURL(this.coverObjectUrl);
        this.coverObjectUrl = '';
        this.$set(this.errors, 'cover', ['Выберите JPG или PNG размером до 5 МБ.']);
        event.target.value = '';
        this.focusErrors();
        return;
      }
      if (this.coverObjectUrl) URL.revokeObjectURL(this.coverObjectUrl);
      this.coverFile = file;
      this.coverObjectUrl = URL.createObjectURL(file);
    },
    async uploadCover() {
      if (!this.coverFile) return;
      const payload = new FormData();
      payload.append('cover', this.coverFile);
      const response = await this.request('post', '/register-org/cover', payload, { headers: { 'Content-Type': 'multipart/form-data' } });
      this.hydrateState(response.state, true);
      this.coverFile = null;
      if (this.coverObjectUrl) URL.revokeObjectURL(this.coverObjectUrl);
      this.coverObjectUrl = '';
    },
    async submitStep() {
      if (this.loading) return;
      const step = this.currentStep;
      if (['confirm', 'success'].includes(step) && !this.canSubmitLaunch) return;
      this.loading = true;
      this.clearErrors();
      try {
        await this.flushDraft();
        if (step === 'account') {
          const response = await this.request('post', '/register-org/steps/account', { account: this.form.account });
          this.form.account.password = '';
          this.hydrateState(response.state);
          if (this.currentStep === 'account') this.currentStep = 'event';
        } else if (step === 'event') {
          await this.saveSection('event');
          this.currentStep = 'registration';
        } else if (step === 'registration') {
          await this.saveSection('registration');
          this.currentStep = 'confirm';
        } else {
          const response = await this.request('post', '/register-org/complete', { intent: this.launchIntent });
          this.hydrateState(response.state, true);
          this.currentStep = 'success';
        }
        this.saveStatus = 'Черновик сохранён в аккаунте';
      } catch (error) { this.showError(error); }
      finally { this.loading = false; }
    },
    async refreshState() {
      if (this.loading) return;
      this.loading = true;
      this.clearErrors();
      try {
        const response = await this.request('get', '/register-org/state');
        this.hydrateState(response.state, true);
      } catch (error) { this.showError(error); }
      finally { this.loading = false; }
    },
    showError(error) {
      const status = error.response && error.response.status;
      const errorType = !status ? 'network' : ({ 401: 'authentication', 403: 'access', 422: 'validation' }[status] || 'server');
      this.trackAction('error', { error_type: errorType });
      const response = error.response && error.response.data;
      this.updateCsrf(response && response.csrf_token);
      this.errors = response && response.errors ? response.errors : {};
      if (!Object.keys(this.errors).length) this.generalError = 'Не удалось сохранить изменения. Проверьте соединение и попробуйте ещё раз.';
      this.focusErrors();
    },
    focusHeading() {
      this.$nextTick(() => {
        const heading = this.$refs.stepHeading;
        if (!heading) return;
        heading.focus({ preventScroll: true });
        heading.scrollIntoView({ block: 'start' });
      });
    },
    focusErrors() {
      this.$nextTick(() => {
        const summary = this.$refs.errorSummary;
        if (!summary) return;
        summary.focus({ preventScroll: true });
        summary.scrollIntoView({ block: 'start' });
      });
    },
    warnUnsaved(event) {
      if (!this.dirty && !this.coverFile) return;
      event.preventDefault();
      event.returnValue = '';
    },
    formatDate(value) {
      if (!value) return 'Дата не указана';
      const date = new Date(`${value.slice(0, 10)}T12:00:00`);
      return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
    },
    formatDateTime(value) {
      if (!value) return 'после уточнения даты';
      return `${this.formatDate(value)} в ${value.slice(11, 16)}`;
    },
    async shareEvent() {
      const url = this.publicEventUrl;
      if (!url) return;
      if (navigator.share) {
        try {
          await navigator.share({ title: this.summary.event_name, url });
          this.trackAction('share', { method: 'native' });
          return;
        }
        catch (error) { if (error.name === 'AbortError') return; }
      }
      try {
        if (navigator.clipboard) await navigator.clipboard.writeText(url);
        else {
          this.$refs.shareLink.focus();
          this.$refs.shareLink.select();
          if (!document.execCommand('copy')) throw new Error('copy unavailable');
        }
        this.copySuccess = true;
        this.trackAction('share', { method: 'clipboard' });
        this.copyTimer = window.setTimeout(() => { this.copySuccess = false; }, 3000);
      } catch (error) {
        this.$refs.shareLink.focus();
        this.$refs.shareLink.select();
        this.generalError = 'Выделили ссылку — скопируйте её через меню телефона или клавиатуру.';
      }
    },
    trackAction(action, params = {}) {
      this.analytics.action(action, Object.assign({ step: this.currentStep }, params));
    }
  }
};
</script>

<style scoped lang="scss">
.organiser-wizard {
  --wizard-ink: #202124;
  --wizard-muted: #60646b;
  --wizard-line: #dddcd7;
  --wizard-accent: #cf4a26;
  max-width: 880px;
  margin: 0 auto;
  color: var(--wizard-ink);
  font-size: 16px;
  line-height: 1.5;

  *, *::before, *::after { box-sizing: border-box; }
  h1, h2, h3, p { margin: 0; }
  h1 { font-size: clamp(27px, 4vw, 38px); line-height: 1.15; letter-spacing: -.035em; font-weight: 700; }
  h2 { font-size: 21px; line-height: 1.3; font-weight: 650; margin-bottom: 8px; }
  h3 { font-size: 16px; font-weight: 650; }
  a { color: var(--wizard-ink); }
  fieldset { margin: 0; padding: 0; border: 0; min-width: 0; }
  button, input, select, textarea { font: inherit; }
  button, a, input, select, textarea, summary { -webkit-tap-highlight-color: transparent; }
  button { cursor: pointer; }
  button:disabled { cursor: default; opacity: .55; }
  :focus-visible { outline: 3px solid #dd7a51; outline-offset: 3px; }
  h1:focus, .wizard-error:focus { outline: none; }
  h1, .wizard-error { scroll-margin-top: 20px; }
  input:not([type=radio]):not([type=file]), select, textarea {
    display: block; width: 100%; max-width: 100%; min-height: 48px; border: 1px solid #aaa9a3; border-radius: 9px;
    background: #fff; color: var(--wizard-ink); padding: 11px 13px; font-size: 16px; line-height: 1.5;
  }
  input[type=date], input[type=datetime-local] { appearance: none; min-width: 0; }
  input[type=file] { width: 100%; min-height: 48px; font-size: 16px; padding: 6px 0; }
  input[type=file]::file-selector-button { border: 1px solid var(--wizard-line); border-radius: 8px; background: #fff; padding: 10px 12px; margin-right: 10px; color: var(--wizard-ink); }
  textarea { resize: vertical; }
  input::placeholder, textarea::placeholder { color: #777b80; }
  label, .wizard-label { display: block; margin-bottom: 7px; font-weight: 600; }
  label span { font-size: 14px; font-weight: 400; color: var(--wizard-muted); }
}
.wizard-topbar { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; padding: 22px 4px; }
.wizard-brand { font-size: 18px; font-weight: 800; letter-spacing: -.06em; text-decoration: none; }
.wizard-help { margin-left: auto; display: inline-flex; align-items: center; min-height: 44px; font-size: 14px; text-decoration: underline; text-underline-offset: 3px; }
.wizard-settings-link { margin-left: auto; display: inline-flex; align-items: center; min-height: 44px; font-size: 14px; text-decoration: underline; text-underline-offset: 3px; }
.wizard-settings-link + .wizard-help { margin-left: 0; }
.wizard-save-status { color: var(--wizard-muted); font-size: 13px; }
.wizard-main { background: #fff; border: 1px solid #e5e3dc; border-radius: 20px; padding: 36px 40px 0; box-shadow: 0 10px 40px rgba(44, 36, 24, .035); }
.wizard-heading { margin-bottom: 30px; }
.wizard-eyebrow { color: #9b4329; font-size: 12px; text-transform: uppercase; font-weight: 700; letter-spacing: .08em; margin-bottom: 12px !important; }
.wizard-intro { color: var(--wizard-muted); margin-top: 12px !important; max-width: 620px; }
.wizard-progress { display: flex; gap: 8px; margin-bottom: 20px; }
.wizard-progress__step { display: flex; align-items: center; justify-content: center; gap: 10px; flex: 1; min-height: 52px; border: 1px solid var(--wizard-line); border-radius: 12px; background: transparent; color: var(--wizard-muted); font-size: 14px !important; }
.wizard-progress__step:disabled { opacity: 1; }
.wizard-progress__step.is-current { color: var(--wizard-ink); background: #fff; border-color: #c45a34; }
.wizard-progress__step.is-done { color: #376448; }
.wizard-progress__number { display: flex; align-items: center; justify-content: center; flex-shrink: 0; width: 27px; height: 27px; border-radius: 50%; background: #eeeae2; font-weight: 650; }
.is-current .wizard-progress__number { background: #cf4a26; color: #fff; }
.is-done .wizard-progress__number { background: #e3ece3; }
.wizard-progress__mobile { display: none; }
.wizard-field { margin-bottom: 23px; min-width: 0; }
.wizard-field-error { margin-top: 6px !important; color: #a22e25; font-size: 14px; }
.wizard-grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 0 20px; }
.wizard-hint { color: var(--wizard-muted); font-size: 14px; line-height: 1.5; margin-top: 7px !important; }
.wizard-note { background: #f6f4ef; border-radius: 10px; padding: 16px; color: var(--wizard-muted); font-size: 14px; }
.wizard-section { margin-top: 30px; padding-top: 26px; border-top: 1px solid #eeeae3; }
.wizard-section > .wizard-hint { margin-bottom: 20px !important; }
.wizard-button { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 48px; padding: 11px 18px; border: 1px solid transparent; border-radius: 10px; font-size: 15px !important; font-weight: 600; text-align: center; line-height: 1.4; white-space: normal; text-decoration: none !important; }
.wizard-button--primary { background: #222426; color: #fff !important; }
.wizard-button--primary:hover { background: #3a3c3e; }
.wizard-button--secondary { background: #fff; color: var(--wizard-ink); border-color: var(--wizard-line); }
.wizard-button--secondary:hover { background: #f6f4ef; }
.wizard-text-button, .wizard-text-link { display: inline-flex; align-items: center; min-height: 44px; padding: 5px 0; font-size: 14px !important; font-weight: 500; background: transparent; border: 0; text-decoration: underline; text-underline-offset: 3px; text-align: left; }
.wizard-actions { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 22px 0 max(24px, env(safe-area-inset-bottom)); margin-top: 26px; border-top: 1px solid #eeeae3; }
.wizard-account-modes { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; border-radius: 11px; padding: 4px; background: #f0eee8; margin-bottom: 26px; }
.wizard-account-modes button { border: 0; border-radius: 8px; background: transparent; min-height: 44px; padding: 8px; font-size: 14px; color: var(--wizard-muted); }
.wizard-account-modes button[aria-pressed=true] { background: #fff; color: var(--wizard-ink); box-shadow: 0 1px 4px #0000000b; }
.wizard-password { position: relative; }
.wizard-password input { padding-right: 95px !important; }
.wizard-password button { position: absolute; right: 8px; top: 3px; bottom: 3px; min-width: 75px; min-height: 44px; border: 0; background: transparent; font-size: 13px; color: var(--wizard-muted); }
.wizard-cover { display: flex; align-items: center; gap: 20px; border: 1px dashed #c6c1b5; border-radius: 12px; padding: 18px; background: #fcfbf8; }
.wizard-cover img { width: 140px; height: 95px; border-radius: 8px; object-fit: cover; }
.wizard-cover__controls { min-width: 0; flex: 1; }
.wizard-choices { display: flex; gap: 12px; }
.organiser-wizard .wizard-choice { display: flex; align-items: center; gap: 10px; flex: 1; margin: 0; min-height: 52px; padding: 12px 14px; border: 1px solid var(--wizard-line); border-radius: 10px; cursor: pointer; }
.organiser-wizard .wizard-choice span { color: var(--wizard-ink); font-size: 15px; font-weight: 500; }
.wizard-choice input { width: 18px; height: 18px; accent-color: #252725; flex-shrink: 0; }
.wizard-choice.is-selected { border-color: #484b43; background: #f6f7f2; }
.wizard-notice { padding: 20px; background: #f8f3e9; border: 1px solid #ece2ce; border-radius: 12px; margin: 22px 0; }
.wizard-notice p { color: #665f50; margin-top: 7px; font-size: 15px; }
.wizard-notice--success { background: #f0f5ed; border-color: #dce5d5; }
.wizard-error { border: 1px solid #ddaaa3; border-radius: 10px; background: #fff4f1; padding: 18px; margin-bottom: 24px; color: #8a3027; }
.wizard-error ul { padding-left: 20px; margin: 6px 0 0; }
.wizard-race { padding: 20px 20px 0; border: 1px solid var(--wizard-line); border-radius: 12px; margin: 16px 0; }
.wizard-race__heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 18px; min-height: 32px; }
.wizard-race__heading button { color: #99402e; }
.wizard-readonly { min-height: 48px; padding: 12px; border-radius: 9px; background: #f4f3ef; color: #55594f; font-size: 14px; }
.wizard-dates { margin-top: 22px; }
.wizard-form-preview { padding: 18px 20px; background: #f8f7f3; border: 1px solid #e7e4dd; border-radius: 12px; margin-top: 24px; }
.wizard-form-preview summary { min-height: 44px; display: list-item; align-content: center; font-weight: 600; cursor: pointer; }
.wizard-form-preview ul { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; padding-left: 20px; margin: 18px 0; font-size: 14px; }
.wizard-review { border: 1px solid var(--wizard-line); border-radius: 14px; overflow: hidden; }
.wizard-review__cover { display: block; width: 100%; max-height: 300px; object-fit: cover; }
.wizard-review__content { padding: 24px; }
.wizard-review__content > p { color: var(--wizard-muted); }
.wizard-review-races { list-style: none; padding: 0; margin: 18px 0 0; border-top: 1px solid #eeeae3; }
.wizard-review-races li { display: flex; flex-direction: column; gap: 4px; padding: 13px 0; border-bottom: 1px solid #eeeae3; }
.wizard-review-races span, .wizard-review-races small { color: var(--wizard-muted); font-size: 14px; }
.wizard-facts { margin: 22px 0 8px; }
.wizard-facts > div { display: grid; grid-template-columns: 80px minmax(0, 1fr); gap: 14px; padding: 9px 0; }
.wizard-facts dt { color: var(--wizard-muted); font-size: 14px; font-weight: 400; }
.wizard-facts dd { margin: 0; overflow-wrap: anywhere; }
.wizard-preview-links { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin: 20px 0; }
.wizard-cabinet { padding-bottom: 36px; }
.wizard-share { padding: 23px; border: 1px solid var(--wizard-line); border-radius: 12px; margin: 24px 0; }
.wizard-share input { color: var(--wizard-muted) !important; }
.wizard-inline-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 12px 20px; margin-top: 16px; }
.wizard-stats { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin: 24px 0; }
.wizard-stat { display: flex; flex-direction: column; gap: 4px; padding: 20px; border: 1px solid var(--wizard-line); border-radius: 12px; text-decoration: none !important; }
.wizard-stat strong { font-size: 32px; font-weight: 650; line-height: 1.2; }
.wizard-stat span { color: var(--wizard-muted); font-size: 14px; }
.wizard-cabinet-actions { display: flex; flex-wrap: wrap; gap: 12px; padding-top: 10px; margin-bottom: 20px; }
.wizard-cabinet-actions > .wizard-text-link { width: 100%; }
@media (max-width: 600px) {
  .wizard-topbar { padding: 12px 2px; gap: 2px 14px; }
  .wizard-save-status { order: 3; flex-basis: 100%; min-height: 20px; font-size: 12px; }
  .wizard-main { padding: 24px 18px 0; border-radius: 15px; }
  .wizard-heading { margin-bottom: 25px; }
  .wizard-intro { font-size: 15px; }
  .wizard-progress { flex-wrap: wrap; gap: 5px; margin: 12px 0; }
  .wizard-progress__step { min-height: 44px; height: 44px; border: 0; border-bottom: 3px solid #ddd9cf; background: transparent; border-radius: 0; padding: 0 0 5px; }
  .wizard-progress__step.is-current { background: transparent; border-bottom-color: #cf4a26; }
  .wizard-progress__step.is-done { border-bottom-color: #6d8667; }
  .wizard-progress__label { display: none; }
  .wizard-progress__mobile { display: block; width: 100%; font-size: 13px; color: var(--wizard-muted); padding-top: 7px; }
  .wizard-grid { grid-template-columns: minmax(0, 1fr); gap: 0; }
  .wizard-field { margin-bottom: 22px; }
  .wizard-cover { flex-direction: column; align-items: stretch; padding: 14px; gap: 12px; }
  .wizard-cover img { width: 100%; height: 170px; }
  .wizard-actions { display: grid; grid-template-columns: 1fr; gap: 8px; padding-top: 16px; margin-top: 20px; }
  .wizard-actions > .wizard-button--primary { grid-row: 1; width: 100%; min-height: 52px; }
  .wizard-actions > .wizard-text-button, .wizard-actions > .wizard-text-link { justify-content: center; }
  .wizard-actions > span { display: none; }
  .wizard-race { padding: 16px 14px 0; }
  .wizard-notice { padding: 17px; }
  .wizard-review__content { padding: 19px; }
  .wizard-facts > div { grid-template-columns: 55px minmax(0, 1fr); gap: 12px; }
  .wizard-share { padding: 17px; }
  .wizard-inline-actions > .wizard-button, .wizard-cabinet-actions > .wizard-button { width: 100%; }
  .wizard-stats { gap: 10px; }
  .wizard-stat { padding: 16px 13px; }
  .wizard-stat span { font-size: 13px; }
  .wizard-preview-links > .wizard-button { width: 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .organiser-wizard * { scroll-behavior: auto !important; }
}
</style>
